import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { formatNZD } from "@/lib/format";
import { buildAdPath } from "@/lib/slugs";
import { MapPin, ChevronRight } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&auto=format&fit=crop&q=60";
const MAX = 7;

/**
 * Horizontal strip of "More listings by <seller>" shown at the bottom of the ad detail page.
 * Fetches up to MAX + 1 listings so we can safely drop the current ad without falling below MAX.
 */
export default function MoreBySellerStrip({ sellerId, sellerName, currentAdId }) {
  const { data, isLoading } = useQuery({
    queryKey: ["by-seller", sellerId],
    queryFn: async () => {
      const { data } = await sharedApi.get("/classifieds/listings", {
        params: { user_id: sellerId, limit: MAX + 1 },
      });
      return data;
    },
    enabled: !!sellerId,
    staleTime: 60_000,
  });

  if (!sellerId) return null;

  const items = ((data?.listings || data?.items || []).filter((l) => l.id !== currentAdId)).slice(0, MAX);
  // Hide entirely while loading — most sellers only have a few listings, so skeletons
  // usually flicker to nothing. Once we know the real count we render (or stay hidden).
  if (isLoading || items.length === 0) return null;

  return (
    <section className="mt-10" data-testid="more-by-seller-strip">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display text-xl md:text-2xl font-bold text-slate-900">
          More listings {sellerName ? <>by <span className="text-[color:var(--brand-blue)]">{sellerName}</span></> : "by this seller"}
        </h2>
        <Link
          to={`/seller/${encodeURIComponent(sellerId)}`}
          data-testid="more-by-seller-view-all"
          className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--brand-blue)] hover:underline"
        >
          View all <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="flex gap-3 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1 snap-x">
        {items.map((l) => (
          <Link
            key={l.id}
            to={buildAdPath(l)}
            data-testid={`more-by-seller-item-${l.id}`}
            className="shrink-0 w-56 snap-start rounded-2xl overflow-hidden bg-white shadow-card hover:shadow-lg border border-soft transition-shadow"
          >
            <div className="aspect-[4/3] bg-slate-100 relative">
              <img
                src={l.thumb || (l.images && l.images[0]) || FALLBACK}
                alt={l.title}
                loading="lazy"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.src = FALLBACK; }}
              />
              {l.is_featured && (
                <span className="absolute top-2 left-2 rounded-full bg-[color:var(--brand-orange)] text-white text-[10px] font-bold uppercase tracking-widest px-2 py-0.5">
                  Featured
                </span>
              )}
            </div>
            <div className="p-3">
              <div className="text-[color:var(--brand-blue)] font-display font-bold text-sm truncate">
                {formatNZD(l.price, l.price_type, l.currency || "NZD")}
              </div>
              <div className="text-slate-800 text-sm line-clamp-2 min-h-[2.6em] mt-0.5">{l.title}</div>
              {(l.city || l.region) && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] text-slate-500">
                  <MapPin className="w-3 h-3" /> {[l.city, l.region].filter(Boolean).join(", ")}
                </div>
              )}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
